import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Upload, CheckCircle2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",", 2)[1] || ""); r.onerror = rej; r.readAsDataURL(file); });

export default function ModuloPazientePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/intake/${token}`).then((r) => setInfo(r.data)).catch(() => setErr("Link non valido o scaduto."));
  }, [token]);

  const upload = async () => {
    setBusy(true); setErr("");
    try {
      const attachments = [];
      for (const f of files) attachments.push({ filename: f.name, mime_type: f.type || "application/octet-stream", data_b64: await toB64(f) });
      await axios.post(`${API}/public/intake/${token}/upload`, { attachments });
      setDone(true);
    } catch (e) { setErr(e?.response?.data?.detail || "Caricamento non riuscito, riprova."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#EEF1F5] flex items-start justify-center p-4 md:p-10" data-testid="modulo-paziente-page">
      <Card className="w-full max-w-lg p-6 rounded-xl border-slate-200 bg-white shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <img src="/icon.svg?v=20" alt="Synappsi" className="h-10 w-10" />
          <div>
            <div className="font-heading font-bold text-slate-900 text-lg">Modulo di accoglienza</div>
            {info && <div className="text-xs text-slate-500">{info.doctor_name}</div>}
          </div>
        </div>
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="modulo-error">{err}</div>}
        {info && !done && (
          <>
            <p className="text-sm text-slate-700" data-testid="modulo-greeting">
              Gentile {info.first_name || "paziente"}, prima della visita La invitiamo a compilare il modulo con i Suoi dati e la Sua storia clinica.
            </p>
            <ol className="text-sm text-slate-700 space-y-3 list-decimal pl-5">
              <li>
                Scarichi il modulo e lo compili (digitando nei campi oppure stampandolo e scrivendo a mano).
                <div className="mt-2"><a href={`${API}/public/intake/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="modulo-download">
                  <Button variant="outline" className="h-10 bg-white"><Download className="h-4 w-4 mr-2" /> Scarica il modulo PDF</Button></a></div>
              </li>
              <li>
                Riconsegni il modulo compilato: carichi il PDF salvato oppure una o più foto nitide del foglio.
                <div className="mt-2 space-y-2">
                  <input type="file" accept="application/pdf,image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))}
                         className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-slate-700"
                         data-testid="modulo-file-input" />
                  {files.length > 0 && <div className="text-xs text-slate-500 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {files.map((f) => f.name).join(", ")}</div>}
                  <Button onClick={upload} disabled={!files.length || busy} className="h-10 bg-[#1E88E5] hover:bg-[#1976D2] text-white" data-testid="modulo-upload-btn">
                    <Upload className="h-4 w-4 mr-2" /> {busy ? "Invio in corso…" : "Invia al medico"}
                  </Button>
                </div>
              </li>
            </ol>
            {info.status === "returned" && <p className="text-xs text-emerald-700">Ha già riconsegnato il modulo: può caricarne una nuova versione se necessario.</p>}
          </>
        )}
        {done && (
          <div className="text-center py-6" data-testid="modulo-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
            <div className="font-semibold text-slate-900">Modulo inviato, grazie!</div>
            <div className="text-sm text-slate-600">Il medico lo troverà già pronto al momento della visita.</div>
          </div>
        )}
        <p className="text-[11px] text-slate-400">I dati sono trasmessi in modo protetto e utilizzati esclusivamente dal medico indicato per la Sua visita.</p>
      </Card>
    </div>
  );
}
